//client/src/components/Header.tsx
import { useState, useEffect, useRef } from "react";
import {
  Sprout,
  Wifi,
  WifiOff,
  LogIn,
  LogOut,
  Bell,
  Settings,
  Menu,
  X,
  MessageCircle,
  Download,
} from "lucide-react";
import { Link } from "react-router-dom";
import AuthModal from "./AuthModal.tsx";
import OnlineStatusModal from "./OnlineStatusModal.tsx";
import LanguageToggle from "./LanguageToggle.tsx";
import UserSettingsModal from "./UserSettingsModal.tsx";
import ChatModal from "./ChatModal.tsx";

import { useAuth } from "../contexts/AuthContext.tsx";
import { useLanguage } from "../contexts/LanguageContext.tsx";
import { supabase } from "../lib/supabaseClient";


// Helper function to check admin status.
function isAdmin(user: any): boolean {
  return user?.email === 'kutloano.moshao111@gmail.com';
}

export default function Header({
  online,
}: {
  online: boolean;
}) {
  const { t } = useLanguage();
  const { user, signOut } = useAuth();
  
  console.log('Header render - user:', user ? 'LOGGED IN' : 'NOT LOGGED IN');
  
  // Track user state changes
  useEffect(() => {
    console.log('Header useEffect - user changed to:', user ? 'LOGGED IN' : 'NOT LOGGED IN');
  }, [user]);
  const [showAuth, setShowAuth] = useState(false);
  const [showOnline, setShowOnline] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showChat, setShowChat] = useState(false);

  const [showAdminDropdown, setShowAdminDropdown] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);





  // Effect to close dropdowns/mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !(dropdownRef.current as HTMLElement).contains(event.target as Node)
      ) {
        setShowAdminDropdown(false);
      }
      if (
        mobileMenuRef.current &&
        !(mobileMenuRef.current as HTMLElement).contains(
          event.target as Node
        ) &&
        showMobileMenu
      ) {
        setShowMobileMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showMobileMenu]);

  const handleLogout = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error("Logout error:", error);
    }
    setShowMobileMenu(false);
  };



  // Common button styles for consistency - ALL use px-4 for equal width
  const buttonBase =
    "inline-flex items-center font-medium text-sm h-9 transition-colors";
  const primaryButton = `${buttonBase} bg-green-600 text-white hover:bg-green-700 rounded-full px-4`;
  const secondaryButton = `${buttonBase} bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 rounded-full px-4`; // Changed from px-3 to px-4
  const greenBorderButton = `${buttonBase} bg-white text-green-700 border border-green-600 hover:bg-green-50 rounded-full px-4`;
  const onlineButton = `${buttonBase} border bg-white rounded-full px-4 ${
    online ? "border-green-600 text-green-800 hover:bg-green-50" : "border-orange-500 text-orange-800 hover:bg-orange-50"
  }`;

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 border-b border-green-200 bg-white/95 backdrop-blur-sm shadow-sm">
        <div className="container flex items-center justify-between px-4 py-3 mx-auto md:py-4">
          {/* Logo and App Name */}
          <div className="flex items-center space-x-2 md:space-x-3">
            <div className="flex items-center justify-center p-1.5 bg-green-600 rounded-lg md:p-2">
              <Sprout className="w-5 h-5 text-white md:w-6 md:h-6" />
            </div>
            <div>
              <Link
                to="/"
                className="text-xl font-bold text-green-800 hover:underline md:text-2xl"
                onClick={() => setShowMobileMenu(false)}
              >
                {t("header.appName")}
              </Link>
              <div className="text-xs text-green-600 md:text-sm">
                {t("header.tagline")}
              </div>
            </div>
          </div>

          {/* Desktop Navigation (hidden on mobile) */}
          <div className="items-center hidden space-x-2 md:flex">
            {/* Language Toggle */}
            <LanguageToggle />

            {/* Download APK Button */}
            <a
              href="/agrosense-app.apk"
              download="AgroSense-App.apk"
              className={secondaryButton}
              title="Download Android App"
            >
              <Download className="w-4 h-4" />
              <span className="ml-2">Get Android App</span>
            </a>

            {/* Online/Offline button */}
            <div className={onlineButton}>
              {online ? <Wifi className="w-4 h-4" /> : <WifiOff className="w-4 h-4" />}
              <span className="ml-2">
                {online ? t("header.online") : t("header.offline")}
              </span>
            </div>

            {/* Alerts Notification - Only for Admin */}
            {user && isAdmin(user) && (
              <button
                type="button"
                onClick={() => alert('Alert system coming soon!')}
                className={`${secondaryButton} relative`}
                aria-label="Send Alerts"
              >
                <Bell className="w-4 h-4" />
              </button>
            )}

            {/* User Settings - Only for non-admin users */}
            {user && !isAdmin(user) && (
              <button
                type="button"
                onClick={() => setShowSettings(true)}
                className={secondaryButton}
                aria-label="User Settings"
              >
                <Settings className="w-4 h-4" />
              </button>
            )}

            {/* Sign In/Out */}
            {user ? (
              <button
                type="button"
                onClick={handleLogout}
                className={primaryButton}
              >
                <LogOut className="w-4 h-4" />
                <span className="ml-2">{t("header.signOut")}</span>
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setShowAuth(true)}
                className={primaryButton}
              >
                <LogIn className="w-4 h-4" />
                <span className="ml-2">{t("header.signIn")}</span>
              </button>
            )}

            {/* Admin Dropdown */}
            {user && isAdmin(user) && (
              <div className="relative" ref={dropdownRef}>
                <button
                  type="button"
                  onClick={() => setShowAdminDropdown(!showAdminDropdown)}
                  className={primaryButton}
                  aria-expanded={showAdminDropdown}
                  aria-haspopup="true"
                >
                  <Settings className="w-4 h-4" />
                  <span className="ml-2">{t("header.admin")}</span>
                </button>

                {showAdminDropdown && (
                  <div className="absolute right-0 z-10 w-48 py-1 mt-2 bg-white border border-gray-200 rounded-md shadow-lg">
                    <Link
                      to="/admin"
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setShowAdminDropdown(false)}
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      {t("header.adminDashboard")}
                    </Link>
                    <Link
                      to="/admin/send-notification"
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setShowAdminDropdown(false)}
                    >
                      <Bell className="w-4 h-4 mr-2" />
                      {t("header.sendNotification")}
                    </Link>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Mobile Menu Toggle */}
          <div className="flex items-center md:hidden">
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="p-2 text-green-800 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              aria-label="Toggle menu"
            >
              {showMobileMenu ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu Content */}
        {showMobileMenu && (
          <div
            ref={mobileMenuRef}
            className="px-4 py-4 border-t border-green-200 md:hidden bg-white/95 backdrop-blur-sm"
          >
            <div className="flex flex-col space-y-3">
              {/* Language Toggle */}
              <LanguageToggle className={`${greenBorderButton} w-full justify-start`} />

              {/* Download APK Button */}
              <a
                href="/agrosense-app.apk"
                download="AgroSense-App.apk"
                className={`${secondaryButton} w-full justify-start`}
                onClick={() => setShowMobileMenu(false)}
              >
                <Download className="w-4 h-4" />
                <span className="ml-2">Download Android App</span>
              </a>

              {/* Online/Offline button */}
              <div className={onlineButton}>
                {online ? <Wifi className="w-4 h-4" /> : <WifiOff className="w-4 h-4" />}
                <span className="ml-2">
                  {online ? t("header.online") : t("header.offline")}
                </span>
              </div>

              {/* Alerts Notification - Only for Admin */}
              {user && isAdmin(user) && (
                <button
                  type="button"
                  onClick={() => {
                    alert('Alert system coming soon!');
                    setShowMobileMenu(false);
                  }}
                  className={secondaryButton}
                >
                  <Bell className="w-4 h-4" />
                  <span className="ml-2">Send Alerts</span>
                </button>
              )}

              {/* User Settings - Only for non-admin users */}
              {user && !isAdmin(user) && (
                <button
                  type="button"
                  onClick={() => {
                    setShowSettings(true);
                    setShowMobileMenu(false);
                  }}
                  className={secondaryButton}
                >
                  <Settings className="w-4 h-4" />
                  <span className="ml-2">{t("header.settings")}</span>
                </button>
              )}

              {/* Sign In/Out */}
              {user ? (
                <button
                  type="button"
                  onClick={handleLogout}
                  className={primaryButton}
                >
                  <LogOut className="w-4 h-4" />
                  <span className="ml-2">{t("header.signOut")}</span>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setShowAuth(true);
                    setShowMobileMenu(false);
                  }}
                  className={primaryButton}
                >
                  <LogIn className="w-4 h-4" />
                  <span className="ml-2">{t("header.signIn")}</span>
                </button>
              )}

              {/* Admin Dropdown */}
              {user && isAdmin(user) && (
                <div className="relative" ref={dropdownRef}>
                  <button
                    type="button"
                    onClick={() => setShowAdminDropdown(!showAdminDropdown)}
                    className={primaryButton}
                    aria-expanded={showAdminDropdown}
                    aria-haspopup="true"
                  >
                    <Settings className="w-4 h-4" />
                    <span className="ml-2">{t("header.admin")}</span>
                  </button>

                  {showAdminDropdown && (
                    <div className="w-full py-1 mt-2 bg-white border border-gray-200 rounded-md shadow-lg">
                      <Link
                        to="/admin"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => {
                          setShowAdminDropdown(false);
                          setShowMobileMenu(false);
                        }}
                      >
                        <Settings className="w-4 h-4 mr-2" />
                        {t("header.adminDashboard")}
                      </Link>
                      <Link
                        to="/admin/send-notification"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => {
                          setShowAdminDropdown(false);
                          setShowMobileMenu(false);
                        }}
                      >
                        <Bell className="w-4 h-4 mr-2" />
                        {t("header.sendNotification")}
                      </Link>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </header>

      <AuthModal open={showAuth} onClose={() => setShowAuth(false)} />
      <OnlineStatusModal
        open={showOnline}
        onClose={() => setShowOnline(false)}
        online={online}
      />
      <UserSettingsModal
        open={showSettings}
        onClose={() => setShowSettings(false)}
        user={user}
      />
      <ChatModal
        open={showChat}
        onClose={() => setShowChat(false)}
        user={user}
      />
    </>
  );
}
